import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { contactFormSchema } from '../../utils/validation';
import { databaseService } from '../../services/database.service';
import { useDatabase } from '../../hooks/useDatabase';
import { useLanguage } from '../../hooks/useLanguage';
import { i18nService } from '../../services/i18n.service';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import toast from 'react-hot-toast';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';

const ContactPage = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: companyInfo } = useDatabase('website/company');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(contactFormSchema)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await databaseService.submitContactForm(data);
      toast.success(t('toast.messageSent'));
      reset();
    } catch (error) {
      toast.error(t('toast.messageFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-display font-bold text-center mb-8">{t('contact.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card>
            <h2 className="text-2xl font-semibold mb-6">{t('contact.formTitle')}</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Input
                label={t('contact.fullName')}
                {...register('name')}
                error={errors.name?.message}
                required
                placeholder={t('contact.fullNamePlaceholder')}
              />

              <Input
                label={t('contact.email')}
                type="email"
                {...register('email')}
                error={errors.email?.message}
                required
                placeholder={t('contact.emailPlaceholder')}
              />

              <Input
                label={t('contact.phone')}
                {...register('phone')}
                error={errors.phone?.message}
                placeholder={t('contact.phonePlaceholder')}
              />

              <Input
                label={t('contact.subject')}
                {...register('subject')}
                error={errors.subject?.message}
                required
                placeholder={t('contact.subjectPlaceholder')}
              />

              <Textarea
                label={t('contact.message')}
                {...register('message')}
                error={errors.message?.message}
                required
                rows={6}
                placeholder={t('contact.messagePlaceholder')}
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <h2 className="text-2xl font-semibold mb-6">{t('contact.getInTouch')}</h2>
              <div className="space-y-4">
                {companyInfo?.phone && (
                  <div className="flex items-start space-x-4">
                    <FaPhone className="text-primary-600 text-xl mt-1" />
                    <div>
                      <h3 className="font-semibold">{t('contact.phoneLabel')}</h3>
                      <p className="text-gray-600">{companyInfo.phone}</p>
                    </div>
                  </div>
                )}

                {companyInfo?.email && (
                  <div className="flex items-start space-x-4">
                    <FaEnvelope className="text-primary-600 text-xl mt-1" />
                    <div>
                      <h3 className="font-semibold">{t('contact.emailLabel')}</h3>
                      <p className="text-gray-600">{companyInfo.email}</p>
                    </div>
                  </div>
                )}

                {companyInfo?.address && (
                  <div className="flex items-start space-x-4">
                    <FaMapMarkerAlt className="text-primary-600 text-xl mt-1" />
                    <div>
                      <h3 className="font-semibold">{t('contact.addressLabel')}</h3>
                      <p className="text-gray-600">
                        {i18nService.getTranslatedField(companyInfo, 'address', currentLanguage)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="bg-primary-50">
              <h3 className="text-xl font-semibold mb-4">{t('contact.whyChooseUs')}</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ {t('contact.benefit1')}</li>
                <li>✓ {t('contact.benefit2')}</li>
                <li>✓ {t('contact.benefit3')}</li>
                <li>✓ {t('contact.benefit4')}</li>
                <li>✓ {t('contact.benefit5')}</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
